/**
 * One client's plan in the master list: who is running it, which plan it is, and how far through.
 *
 * ── The CLIENT is the title and the PLAN is the sub-line, which is the whole point of the scope ─
 * The templates list is titled by the plan because that is what a trainer is choosing between there.
 * Here they are choosing between people, the list is alphabetical by surname, and the plan's name is
 * what qualifies the person. Swapping the two would make the scope a second copy of the first.
 *
 * ── The bar reads the CATEGORY, not the progress ─────────────────────────────────────────────
 * Its width is the percent; its colour is the plan's category, exactly as the row's cover rule is.
 * That is deliberate and it is the canon's: a bar that turned red or green with the number would be
 * a judgement on the client, and this screen does not make one. The percent beside it is the figure,
 * the bar is its shape, and neither says whether 40% is good.
 *
 * ── A paused plan keeps its bar and loses its count ──────────────────────────────────────────
 * Owner decision D7: `Wstrzymany` stands where `12 / 30 treningów` stands on a live plan, and the
 * percent and the bar are unchanged. What paused means is that nothing is moving, not that nothing
 * happened.
 *
 * The row's interaction is `TemplateRow`'s, value for value — `mousemove` rather than `mouseenter`,
 * focus into the row selects it, the whole row is a `Link`, and the ring is drawn on the row rather
 * than on the stretched anchor. That header carries the reasoning; this one does not restate it.
 */

import type { CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';

import { AvatarChip } from '~/shared/ui/AvatarChip';
import { planCategoryColour } from '~/shared/ui/planCategoryColour';
import photoBox from '~/shared/ui/photoBox.module.css';
import type { PhotoReveal } from '~/shared/lib/usePhotoReveal';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ClientPlanRow as ClientPlanRowModel } from '../model/clientPlanRow';
import { CalendarModeIcon, SelfPacedModeIcon } from './PlanLibraryIcons';
import listRow from './listRow.module.css';
import styles from './ClientPlanRow.module.css';

interface ClientPlanRowProps {
  /** The list's key for this row, which is also its arrow-navigation handle. See `ListRows`. */
  readonly rowKey: string;
  readonly row: ClientPlanRowModel;
  /**
   * The client's name as the row draws it — `model/personName`'s answer, so a client the backend
   * composed no name for still gets a link with an accessible name rather than an empty one.
   */
  readonly name: string;
  /** `Kategoria: Siła`, or `null` where the plan carries none. Composed by `ListRows`. */
  readonly categorySpoken: string | null;
  /** `12 / 30 treningów`, or the word for a plan nobody is moving through. Composed by `ListRows`. */
  readonly progressLabel: string;
  /** The percent as the trainer reads it, formatted by the caller. */
  readonly percentLabel: string;
  /** The bar's whole fact in one sentence, because a width is not readable and `40%` has no subject. */
  readonly progressSpoken: string;
  readonly selected: boolean;
  readonly onPoint: () => void;
  /**
   * The list's shared photograph group — every cover in it appears on one frame rather than as its
   * own file lands. Owned by `ListRows`, because the group is the list; see `usePhotoReveal`.
   */
  readonly photo: PhotoReveal;
}

export function ClientPlanRow({
  rowKey,
  row,
  name,
  categorySpoken,
  progressLabel,
  percentLabel,
  progressSpoken,
  selected,
  onPoint,
  photo,
}: ClientPlanRowProps) {
  const ModeIcon = row.schedulingMarker === 'selfPaced' ? SelfPacedModeIcon : CalendarModeIcon;

  return (
    <li
      className={`${String(listRow.row)} ${String(styles.row)} ${
        selected ? String(listRow.rowSelected) : ''
      }`}
      style={planCategoryColour(row.categoryKey)}
      // `undefined` on the row the pane is ALREADY describing: a pointer resting on a row fires
      // `mousemove` sixty to a hundred and twenty times a second, and every one of those was a
      // round of no-op work to select what was already selected.
      //
      // Still `mousemove` and NOT `mouseenter`. A dialog makes the rest of the page `inert`, so no
      // row is hoverable while one is open, and the instant it closes the browser re-evaluates
      // hover and fires `mouseenter` under a pointer that never moved — on the roster that swapped
      // the panel to a client nobody chose and dropped focus to `<body>`.
      onMouseMove={selected ? undefined : onPoint}
      onFocusCapture={onPoint}
    >
      {selected && <span className={listRow.selectionBar} aria-hidden />}

      {/* The name beside it carries the person, so the disc says nothing of its own — that is
        * `AvatarChip`'s standing rule and the reason both of its branches are decorative. */}
      <span className={styles.avatarCell}>
        <AvatarChip
          initials={row.client.initials}
          avatarUrl={row.client.avatarUrl}
          variant="library"
          loading="lazy"
        />
      </span>

      <span className={listRow.cover}>
        <img
          className={`${String(photoBox.photo)} ${String(listRow.coverImage)} ${photo.holding} ${photo.reveal}`}
          src={row.thumbUrl}
          alt=""
          loading="lazy"
          decoding="async"
          // A failure is an arrival — see the twin in `TemplateRow`.
          onLoad={photo.onPhotoSettled}
          onError={photo.onPhotoSettled}
        />
      </span>

      <span className={listRow.identity}>
        <span className={listRow.nameLine}>
          <Link
            className={listRow.rowLink}
            to="/plans/$planId"
            // The client's OWN fork. Opening the template it came from would show the trainer a
            // different document under this client's name.
            params={{ planId: String(row.planTemplateId) }}
            data-plan-row={rowKey}
            title={name}
            // The plan's category is a 2.5px rule under the cover and nothing else, so a reader
            // user meets a facet in the filter panel they cannot hear on a row. `TemplateRow`
            // carries the reasoning for the `aria-label`.
            aria-label={categorySpoken === null ? undefined : `${name}, ${categorySpoken}`}
          >
            {name}
          </Link>
        </span>
        <span className={listRow.sub}>{row.planName}</span>
        {/* Decorative twice over: the width is the percent printed beside it, and the colour is the
          * category already drawn on the cover. The sentence under the figure carries both facts. */}
        <span className={styles.bar} aria-hidden>
          <span
            className={styles.barFill}
            style={{ '--pl-progress': `${String(row.progressPct)}%` } as CSSProperties}
          />
        </span>
      </span>

      <span className={listRow.mode}>
        <ModeIcon className={listRow.modeIcon} />
        <span className={listRow.modeLabel}>{progressLabel}</span>
      </span>

      {/* `40%` on its own is a number with no subject, and the label to its left is a different
        * fact. One sentence is read in place of both marks. */}
      <span className={styles.progress}>
        <span className={styles.percent} aria-hidden>
          {percentLabel}
        </span>
        <span className={hidden.srOnly}>{progressSpoken}</span>
      </span>
    </li>
  );
}
